/**
 * 화면 문구 사전 (PRD2 T-0.6, §12.1 · §13.1).
 * 같은 대상은 한 이름만 쓴다. `avoid`는 그 뜻으로 쓰지 않는 말이다.
 */

export type UiTerm = {
  ko: string;
  en: string;
  /** 이 말이 가리키는 것 */
  meaning: string;
  /** 이 뜻으로 화면에 쓰지 않는 말 */
  avoid: readonly string[];
};

export const UI_TERMS = {
  plan: {
    ko: "플랜",
    en: "Plan",
    meaning: "일정·현황을 담는 프로젝트 하나",
    avoid: ["프로젝트"],
  },
  archive: {
    ko: "아카이브",
    en: "Archive",
    meaning: "읽기 전용으로 동결한다. 복구하면 멤버십과 데이터가 그대로다",
    avoid: ["보관", "종료", "폐기"],
  },
  delete: {
    ko: "삭제",
    en: "Delete",
    meaning: "휴지통으로 보낸다. 30일 안에 복구할 수 있다",
    avoid: ["폐기", "종료", "영구 삭제"],
  },
  purge: {
    ko: "영구 삭제",
    en: "Purge",
    meaning: "휴지통 만료 뒤에 자동으로 지워지는 일. 즉시 지우는 버튼은 없다",
    avoid: ["폐기"],
  },
  transfer: {
    ko: "이전",
    en: "Transfer",
    meaning: "소유자를 다른 멤버로 바꾼다",
    avoid: [],
  },
  leave: {
    ko: "나가기",
    en: "Leave",
    meaning: "내 멤버십을 끝낸다",
    avoid: [],
  },
  remove: {
    ko: "제거",
    en: "Remove",
    meaning: "관리자가 다른 사람의 멤버십을 끝낸다",
    avoid: [],
  },
  suspend: {
    ko: "중지",
    en: "Suspend",
    meaning: "멤버십은 두고 접근만 막는다",
    avoid: [],
  },
  share: {
    ko: "공유",
    en: "Share",
    meaning: "링크로 내보이는 읽기 전용 화면 묶음",
    avoid: ["게시", "공개", "퍼블리시"],
  },
  linkShare: {
    ko: "링크 공유",
    en: "Link share",
    meaning: "계정이 없어도 보는 읽기 전용 링크",
    avoid: ["게시"],
  },
  invite: {
    ko: "멤버 초대",
    en: "Invite",
    meaning: "사람을 워크스페이스나 플랜 멤버로 들인다",
    avoid: [],
  },
  shareAudience: {
    ko: "열람자",
    en: "Viewer of a share",
    meaning: "공유 링크를 여는 사람. 멤버가 아니다",
    avoid: ["게스트"],
  },
  readOnly: {
    ko: "읽기 전용",
    en: "Read-only",
    meaning: "값은 보이지만 고칠 수 없다",
    avoid: [],
  },
  viewSettings: {
    ko: "보기 설정",
    en: "View settings",
    meaning: "간트·시트·캘린더 표시. 이 기기 또는 이 플랜의 보기",
    avoid: [],
  },
  accountSettings: {
    ko: "계정 설정",
    en: "Account settings",
    meaning: "프로필·기기·토큰·초대",
    avoid: [],
  },
  workspaceSettings: {
    ko: "워크스페이스 설정",
    en: "Workspace settings",
    meaning: "워크스페이스의 일반·멤버·보안·공유·감사·위험 영역",
    avoid: [],
  },
  planSettings: {
    ko: "플랜 설정",
    en: "Plan settings",
    meaning: "플랜의 일반·멤버·보안·공유·감사",
    avoid: [],
  },
  systemSettings: {
    ko: "시스템",
    en: "System",
    meaning: "이 기기의 테마·로그·MCP·저장",
    avoid: [],
  },
  owner: { ko: "소유자", en: "Owner", meaning: "워크스페이스 또는 플랜의 최종 책임자", avoid: [] },
  admin: { ko: "관리자", en: "Admin", meaning: "멤버·정책·감사를 관리한다", avoid: [] },
  editor: { ko: "편집자", en: "Editor", meaning: "일정과 작업을 편집한다", avoid: [] },
  contributor: { ko: "기여자", en: "Contributor", meaning: "본인 작업의 진척과 실적만 입력한다", avoid: [] },
  viewerRole: {
    ko: "뷰어",
    en: "Viewer",
    meaning: "플랜을 읽기만 하는 멤버 역할",
    avoid: ["열람자"],
  },
  guest: {
    ko: "게스트",
    en: "Guest",
    meaning: "초대된 플랜만 보는 멤버 유형",
    avoid: ["열람자"],
  },
  liveShare: {
    ko: "최신 연동",
    en: "Live",
    meaning: "저장할 때마다 발행본이 갱신되는 공유",
    avoid: ["실시간"],
  },
  snapshotShare: {
    ko: "고정 발행",
    en: "Snapshot",
    meaning: "한 발행본에 묶여 바뀌지 않는 공유",
    avoid: ["스냅샷"],
  },
  publication: {
    ko: "발행본",
    en: "Publication",
    meaning: "어느 시점의 플랜 상태를 고정한 기록",
    avoid: ["스냅샷"],
  },
} as const satisfies Record<string, UiTerm>;

export type UiTermId = keyof typeof UI_TERMS;
